import { FC, memo, useEffect, useMemo } from "react";
import { BaseMetadataType } from "../../Core/Api/Types/base-metadata-type";
import { DbMetadataFieldInfos } from "../../Core/Api/Types/db-metadata-field-infos";
import MetadataInputBoolSingle from "./MetadataInputTypes/metadata-input-bool";
import MetadataInputBoolMultiLocalized from "./MetadataInputTypes/metadata-input-bool-multi-localized";
import MetadataInputCasts from "./MetadataInputTypes/metadata-input-casts";
import MetadataInputCreditsLocalized from "./MetadataInputTypes/metadata-input-credits-multi-localized";
import MetadataInputDateMultiLocalized from "./MetadataInputTypes/metadata-input-date-multi-localized";
import MetadataInputDateSingle from "./MetadataInputTypes/metadata-input-date-single";
import MetadataInputGenres from "./MetadataInputTypes/metadata-input-genres";
import MetadataInputLangMulti from "./MetadataInputTypes/metadata-input-lang-multi";
import MetadataInputLangSingle from "./MetadataInputTypes/metadata-input-lang-single";
import MetadataInputStringMulti from "./MetadataInputTypes/metadata-input-string-multi";
import MetadataInputStringOrNumberSingle from "./MetadataInputTypes/metadata-input-string-single";
import MetadataInputSynopses from "./MetadataInputTypes/metadata-input-synopses";
import MetadataInputTerritoryCode from "./MetadataInputTypes/metadata-input-territory-code";
import MetadataInputTitles from "./MetadataInputTypes/metadata-input-titles";
import MetadataInputTitlesMultiLocalized from "./MetadataInputTypes/metadata-input-titles-multi-localized";
import MetadataInputCastsText from "./MetadataInputTypes/metadata-input-casts-text";
import MetadataInputSynopsesText from "./MetadataInputTypes/metadata-input-synopses-text";
import MetadataInputRatings from "./MetadataInputTypes/metadata-input-ratings";
import MetadataInputStringMultiByEp from "./MetadataInputTypes/metadata-input-string-mutli-by-ep";
import MetadataInputMultiTerritoryCode from "./MetadataInputTypes/metadata-input-territory-code-multi";

export enum MetadataEditable {
  "DbLanguageCodeMetadataValueInfos",
  "DbLanguageCodeMultiMetadataValueInfos",
  "DbLocalizedLanguageCodeMetadataValueInfos",
  "DbStringMetadataValueInfos",
  "DbStringMultiMetadataValueInfos",
  "DbLongMetadataValueInfos",
  "DbDoubleMetadataValueInfos",
  "DbDateTimeMetadataValueInfos",
  "DbBoolMetadataValueInfos",
  "DbTerritoryCodeMetadataValueInfos",
  "DbTerritoryCodeMultiMetadataValueInfos",
  "DbStringMultiByEpisodeMetadataValueInfos",
  "DbCastsMetadataValueInfos",
  "DbGenreMultiMetadataValueInfos",
  "DbAnyObjectMultiMetadataValueInfos",
}

export type MetadataInputProps = {
  onChange: (
    value: any,
    fieldId: number,
    valueIndex?: number,
    pushAndReplaceWhereCultureIds?: number[],
    dateType?: string
  ) => void;
  data: DbMetadataFieldInfos;
  value: any;
  value2?: any;
  predefinedValues?: boolean;
  cultureId?: number;
  dateRange?: boolean;
  castOrSynoTextField?: boolean;
  onRemoveRow?: (rowIndex: number) => void;
  onDuplicateRows?: (rowIndexes: number[]) => void;
  onAddRow?: () => void;
  onOrderCastRow?: (targetIndex: number, id: number, valueIndex?: number) => void;
  onReset?: (index: number, field: string) => void;
  onUpdateAllRows?: (value: any, field: string) => void;
  onUpdateRowsBelow?: (value: any, index: number, field: string) => void;
  availableLanguages?: any[];
  epNb?: number | null;
  episodesTotalNb?: number;
  titleType?: "Movie" | "Season";
};

const MetadataInputIndex: FC<MetadataInputProps> = (props) => {
  const {
    onChange,
    onOrderCastRow,
    data,
    value,
    value2 = undefined,
    cultureId = undefined,
    predefinedValues = false,
    dateRange = false,
    castOrSynoTextField = undefined,
    onRemoveRow,
    onAddRow,
    onDuplicateRows,
    availableLanguages,
    onReset,
    onUpdateAllRows,
    onUpdateRowsBelow,
    epNb = null,
    episodesTotalNb,
    titleType = "Movie",
  } = props;

  const _component = useMemo(
    () => (
      <>
        {(data.baseType === BaseMetadataType.DbLanguageCodeMetadataValueInfos ||
          data.baseType === BaseMetadataType.DbLocalizedLanguageCodeMetadataValueInfos) && (
          <MetadataInputLangSingle onChange={onChange} data={data} value={value} predefinedValues={predefinedValues} />
        )}
        {(data.baseType === BaseMetadataType.DbStringMetadataValueInfos ||
          data.baseType === BaseMetadataType.DbLongMetadataValueInfos ||
          data.baseType === BaseMetadataType.DbDoubleMetadataValueInfos) && (
          <MetadataInputStringOrNumberSingle
            onChange={onChange}
            data={data}
            value={value}
            predefinedValues={predefinedValues}
          />
        )}
        {data.baseType === BaseMetadataType.DbLanguageCodeMultiMetadataValueInfos && (
          <MetadataInputLangMulti onChange={onChange} data={data} value={value} predefinedValues={predefinedValues} />
        )}
        {(data.baseType === BaseMetadataType.DbStringMultiMetadataValueInfos ||
          data.baseType === BaseMetadataType.DbLongMultiMetadataValueInfos ||
          data.baseType === BaseMetadataType.DbDoubleMultiMetadataValueInfos ||
          data.baseType === BaseMetadataType.DbAnyObjectMultiMetadataValueInfos) && (
          <MetadataInputStringMulti onChange={onChange} data={data} value={value} predefinedValues={predefinedValues} />
        )}
        {data.baseType === BaseMetadataType.DbDateTimeMetadataValueInfos && (
          <MetadataInputDateSingle
            onChange={onChange}
            data={data}
            value={value}
            dateRange={dateRange}
            value2={value2}
          />
        )}
        {data.baseType === BaseMetadataType.DbLocalizedDateTimeMultiMetadataValueInfos && (
          <MetadataInputDateMultiLocalized
            onChange={onChange}
            data={data}
            value={value}
            cultureId={cultureId}
            dateRange={dateRange}
            epNb={epNb}
          />
        )}
        {data.baseType === BaseMetadataType.DbBoolMetadataValueInfos && (
          <MetadataInputBoolSingle onChange={onChange} data={data} value={value} />
        )}
        {data.baseType === BaseMetadataType.DbLocalizedBoolMultiMetadataValueInfos && (
          <MetadataInputBoolMultiLocalized
            onChange={onChange}
            data={data}
            value={value}
            cultureId={cultureId}
            epNb={epNb}
          />
        )}
        {data.baseType === BaseMetadataType.DbCastsMetadataValueInfos &&
          (castOrSynoTextField === undefined || castOrSynoTextField === false) && (
            <MetadataInputCasts
              onChange={onChange}
              data={data}
              value={value && value.length ? value : value?.value && value.value.length ? value.value : value}
              cultureId={cultureId}
              onRemoveRow={onRemoveRow}
              onAddRow={onAddRow}
              onOrderCastRow={onOrderCastRow}
              onDuplicateRows={onDuplicateRows}
              onReset={onReset}
              onUpdateRowsBelow={onUpdateRowsBelow}
              onUpdateAllRows={onUpdateAllRows}
              availableLanguages={availableLanguages}
              epNb={epNb}
            />
          )}
        {data.baseType === BaseMetadataType.DbCastsMetadataValueInfos &&
          (castOrSynoTextField !== undefined || castOrSynoTextField === true) && (
            <MetadataInputCastsText onChange={onChange} data={data} value={value} onRemoveRow={onRemoveRow} />
          )}
        {data.baseType === BaseMetadataType.DbSynopsesMetadataValueInfos &&
          (castOrSynoTextField === undefined || castOrSynoTextField === false) && (
            <MetadataInputSynopses onChange={onChange} data={data} value={value} cultureId={cultureId} epNb={epNb} />
          )}
        {data.baseType === BaseMetadataType.DbSynopsesMetadataValueInfos &&
          (castOrSynoTextField !== undefined || castOrSynoTextField === true) && (
            <MetadataInputSynopsesText onChange={onChange} data={data} value={value} />
          )}
        {data.baseType === BaseMetadataType.DbTerritoryCodeMetadataValueInfos && (
          <MetadataInputTerritoryCode onChange={onChange} data={data} value={value} />
        )}
        {data.baseType === BaseMetadataType.DbTerritoryCodeMultiMetadataValueInfos && (
          <MetadataInputMultiTerritoryCode onChange={onChange} data={data} value={value} />
        )}
        {data.baseType === BaseMetadataType.DbTitlesMetadataValueInfos && (
          <MetadataInputTitles onChange={onChange} data={data} value={value} />
        )}
        {data.baseType === BaseMetadataType.DbLocalizedTitlesMultiMetadataValueInfos && (
          <MetadataInputTitlesMultiLocalized
            onChange={onChange}
            data={data}
            value={value}
            cultureId={cultureId}
            epNb={epNb}
          />
        )}
        {data.baseType === BaseMetadataType.DbGenreMetadataValueInfos && (
          <MetadataInputGenres onChange={onChange} data={data} value={value} multiple={false} />
        )}
        {data.baseType === BaseMetadataType.DbGenreMultiMetadataValueInfos && (
          <MetadataInputGenres onChange={onChange} data={data} value={value} multiple={true} />
        )}
        {data.baseType === BaseMetadataType.DbLocalizedCreditsMultiMetadataValueInfos && (
          <MetadataInputCreditsLocalized
            onChange={onChange}
            data={data}
            value={value}
            cultureId={cultureId}
            epNb={epNb}
          />
        )}
        {data.baseType === BaseMetadataType.DbLocalizedRatingMultiMetadataValueInfos && (
          <MetadataInputRatings
            onChange={onChange}
            data={data}
            value={value}
            cultureId={cultureId}
            epNb={epNb}
            titleType={titleType}
          />
        )}
        {data.baseType === BaseMetadataType.DbStringMultiByEpisodeMetadataValueInfos && (
          <MetadataInputStringMultiByEp
            onChange={onChange}
            data={data}
            value={value}
            episodesTotalNb={episodesTotalNb}
          />
        )}
      </>
    ),
    [onChange, data, value, epNb]
  );
  return <>{_component}</>;
};

export const MetadataInput = memo(MetadataInputIndex);
export default MetadataInputIndex;
